import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from '../apiServices/axios';
import { INTELLIGENCE_INSIGHTS_V2_HIGHLIGHTS } from '../apiServices/api_routes';
import { highlightsPayloadToViewModel } from '../utils/intelligenceInsightsHighlightsViewModel';
import { insightsContextKey, withInsightsContextQuery } from '../utils/intelligenceInsightsContext';

export function useIntelligenceInsightsHighlights(projectId, { enabled = true, analysisContext = null, analysisId = null } = {}) {
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);
    const payloadRef = useRef(null);
    payloadRef.current = payload;

    const contextKey = insightsContextKey(analysisContext, analysisId);

    useEffect(() => {
        setPayload(null);
        payloadRef.current = null;
    }, [contextKey]);

    const fetchHighlights = useCallback(async () => {
        if (!projectId) return;
        // Keep existing cards visible during refresh — only block UI on first load.
        if (payloadRef.current == null) setLoading(true);
        setError(null);
        try {
            const url = withInsightsContextQuery(
                INTELLIGENCE_INSIGHTS_V2_HIGHLIGHTS(projectId),
                analysisContext,
                { analysisId },
            );
            const { data } = await axiosInstance.get(url);
            setPayload(data || null);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Could not load Highlights.');
            setPayload(null);
        } finally {
            setLoading(false);
        }
    }, [projectId, contextKey, analysisContext, analysisId]);

    useEffect(() => {
        if (enabled) void fetchHighlights();
    }, [enabled, fetchHighlights]);

    const generateHighlights = useCallback(
        async ({ forceScaffold = false } = {}) => {
            if (!projectId) return null;
            setGenerating(true);
            setError(null);
            try {
                const { data } = await axiosInstance.post(
                    withInsightsContextQuery(
                        INTELLIGENCE_INSIGHTS_V2_HIGHLIGHTS(projectId),
                        analysisContext,
                    ),
                    {
                        force_scaffold: forceScaffold,
                    },
                );
                setPayload(data || null);
                return data;
            } catch (err) {
                const detail = err?.response?.data?.detail;
                const message = typeof detail === 'string' ? detail : 'Highlights generation failed.';
                setError(message);
                throw err;
            } finally {
                setGenerating(false);
            }
        },
        [projectId, analysisContext],
    );

    const highlightsData = useMemo(() => {
        const vm = highlightsPayloadToViewModel(payload);
        if (vm) return vm;
        return {
            status: 'not_generated',
            title: 'Highlights',
            subtitle: 'Headline conclusions across Team, Technology and Cybersecurity.',
            metaLine: loading ? 'Loading…' : '',
            cards: [],
            emptyState: null,
            warnings: [],
            moduleActions: [],
            isPopulated: false,
            isFailed: false,
            isGenerating: false,
            isNotGenerated: true,
        };
    }, [payload, loading]);

    return {
        highlightsData,
        payload,
        loading,
        generating,
        error,
        fetchHighlights,
        generateHighlights,
    };
}

export default useIntelligenceInsightsHighlights;
